import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaCreditCard, FaLock, FaSpinner, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import api from '../api/api';

const CyberSource = ({ 
  orderNumber, 
  price, 
  sellerId, 
  sellerName, 
  shippingInfo, 
  currencySymbol,
  currency = 'USD' // Default currency, will be overridden by seller config
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    number: '',
    expirationMonth: '',
    expirationYear: '',
    securityCode: '',
    cardholderName: ''
  });
  const [billingAddress, setBillingAddress] = useState({
    firstName: shippingInfo?.name?.split(' ')[0] || 'John',
    lastName: shippingInfo?.name?.split(' ').slice(1).join(' ') || 'Doe',
    address1: shippingInfo?.address || '123 Main Street',
    city: shippingInfo?.city || 'San Francisco',
    state: shippingInfo?.province || shippingInfo?.state || shippingInfo?.stateCode || 'CA',
    postalCode: shippingInfo?.post || shippingInfo?.postalCode || shippingInfo?.zipCode || '94105',
    country: shippingInfo?.country || 'US',
    email: shippingInfo?.email || 'john.doe@example.com',
    phoneNumber: shippingInfo?.phone || '555-123-4567'
  });

  // Debug log to see what shipping info is available
  console.log('CyberSource shippingInfo:', shippingInfo);

  // State validation and conversion function
  const validateAndConvertState = (state) => {
    if (!state || state.trim().length === 0) {
      return { isValid: false, message: 'State is required' };
    }

    const stateCode = state.trim().toUpperCase();
    
    // US State codes mapping
    const stateMap = {
      'ALABAMA': 'AL', 'ALASKA': 'AK', 'ARIZONA': 'AZ', 'ARKANSAS': 'AR', 'CALIFORNIA': 'CA',
      'COLORADO': 'CO', 'CONNECTICUT': 'CT', 'DELAWARE': 'DE', 'FLORIDA': 'FL', 'GEORGIA': 'GA',
      'HAWAII': 'HI', 'IDAHO': 'ID', 'ILLINOIS': 'IL', 'INDIANA': 'IN', 'IOWA': 'IA',
      'KANSAS': 'KS', 'KENTUCKY': 'KY', 'LOUISIANA': 'LA', 'MAINE': 'ME', 'MARYLAND': 'MD',
      'MASSACHUSETTS': 'MA', 'MICHIGAN': 'MI', 'MINNESOTA': 'MN', 'MISSISSIPPI': 'MS', 'MISSOURI': 'MO',
      'MONTANA': 'MT', 'NEBRASKA': 'NE', 'NEVADA': 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ',
      'NEW MEXICO': 'NM', 'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', 'OHIO': 'OH',
      'OKLAHOMA': 'OK', 'OREGON': 'OR', 'PENNSYLVANIA': 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
      'SOUTH DAKOTA': 'SD', 'TENNESSEE': 'TN', 'TEXAS': 'TX', 'UTAH': 'UT', 'VERMONT': 'VT',
      'VIRGINIA': 'VA', 'WASHINGTON': 'WA', 'WEST VIRGINIA': 'WV', 'WISCONSIN': 'WI', 'WYOMING': 'WY',
      'DISTRICT OF COLUMBIA': 'DC'
    };

    // If it's already a 2-letter code, validate it
    if (stateCode.length === 2) {
      const validCodes = Object.values(stateMap);
      if (validCodes.includes(stateCode)) {
        return { isValid: true, stateCode: stateCode };
      }
    }

    // Try to convert full state name to code
    const convertedCode = stateMap[stateCode];
    if (convertedCode) {
      return { isValid: true, stateCode: convertedCode };
    }

    return { 
      isValid: false, 
      message: `"${state}" is not a valid US state. Please use a 2-letter state code (e.g., CA, NY, TX) or full state name.` 
    };
  };

  // Update billing address when shipping info changes
  React.useEffect(() => {
    if (shippingInfo) {
      setBillingAddress(prev => ({
        ...prev,
        firstName: shippingInfo?.name?.split(' ')[0] || prev.firstName,
        lastName: shippingInfo?.name?.split(' ').slice(1).join(' ') || prev.lastName,
        address1: shippingInfo?.address || prev.address1,
        city: shippingInfo?.city || prev.city,
        state: shippingInfo?.province || shippingInfo?.state || shippingInfo?.stateCode || prev.state,
        postalCode: shippingInfo?.post || shippingInfo?.postalCode || shippingInfo?.zipCode || prev.postalCode,
        country: shippingInfo?.country || prev.country,
        email: shippingInfo?.email || prev.email,
        phoneNumber: shippingInfo?.phone || prev.phoneNumber
      }));
    }
  }, [shippingInfo]);

  const handleInputChange = (field, value) => {
    if (field.startsWith('card.')) {
      const cardField = field.split('.')[1];
      setCardDetails(prev => ({
        ...prev,
        [cardField]: value
      }));
    } else {
      setBillingAddress(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const formatCardNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Add spaces every 4 digits
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    setCardDetails(prev => ({
      ...prev,
      number: formatted
    }));
  };

  const handleExpiryChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 2) {
      setCardDetails(prev => ({
        ...prev,
        expirationMonth: value
      }));
    } else if (value.length <= 4) {
      setCardDetails(prev => ({
        ...prev,
        expirationMonth: value.slice(0, 2),
        expirationYear: value.slice(2)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!cardDetails.number || !cardDetails.expirationMonth || !cardDetails.expirationYear || !cardDetails.securityCode) {
        throw new Error('Please fill in all card details');
      }

      if (!billingAddress.firstName || !billingAddress.lastName || !billingAddress.email) {
        throw new Error('Please fill in all billing information');
      }

      // Validate field lengths for CyberSource requirements
      if (billingAddress.firstName.trim().length < 2) {
        throw new Error('First name must be at least 2 characters long');
      }
      if (billingAddress.lastName.trim().length < 2) {
        throw new Error('Last name must be at least 2 characters long');
      }
      if (billingAddress.address1 && billingAddress.address1.trim().length < 5) {
        throw new Error('Address must be at least 5 characters long');
      }
      if (billingAddress.city && billingAddress.city.trim().length < 2) {
        throw new Error('City must be at least 2 characters long');
      }
      if (billingAddress.state && billingAddress.state.trim().length < 2) {
        throw new Error('State must be at least 2 characters long');
      }

      // Validate and convert state to proper format for CyberSource
      const stateValidation = validateAndConvertState(billingAddress.state);
      if (!stateValidation.isValid) {
        throw new Error(`Invalid state: ${stateValidation.message}`);
      }

      // Validate card number format
      const cleanCardNumber = cardDetails.number.replace(/\s/g, '');
      if (!/^\d{13,19}$/.test(cleanCardNumber)) {
        throw new Error('Please enter a valid card number');
      }

      // Validate expiration date
      const expMonth = parseInt(cardDetails.expirationMonth);
      if (expMonth < 1 || expMonth > 12) {
        throw new Error('Please enter a valid expiration month');
      }

      const expYear = parseInt(cardDetails.expirationYear.length === 2 ? 
        `20${cardDetails.expirationYear}` : cardDetails.expirationYear);
      const currentYear = new Date().getFullYear();
      if (expYear < currentYear || expYear > currentYear + 20) {
        throw new Error('Please enter a valid expiration year');
      }

      // Validate CVV
      if (!/^\d{3,4}$/.test(cardDetails.securityCode)) {
        throw new Error('Please enter a valid security code');
      }

      // Prepare payment data with converted state
      const paymentData = {
        orderNumber,
        amount: Math.round(price * 100), // Convert dollars to cents
        currency: currency, // Use dynamic currency from seller config
        cardDetails: {
          number: cardDetails.number.replace(/\s/g, ''), // Remove spaces
          expirationMonth: cardDetails.expirationMonth,
          expirationYear: cardDetails.expirationYear,
          securityCode: cardDetails.securityCode
        },
        billingAddress: {
          ...billingAddress,
          state: stateValidation.stateCode // Use the converted state code
        }
      };

      // Call CyberSource payment API
      const response = await api.post('/payment/create-cybersource-payment', paymentData);

      if (response.data.success) {
        setSuccess(true);
        
        // Store payment details in localStorage for confirmation page
        localStorage.setItem('cybersourcePayment', JSON.stringify({
          transactionId: response.data.transactionId,
          status: response.data.status,
          amount: price,
          orderNumber: orderNumber
        }));
        
        // Redirect to confirmation page with payment method parameter
        setTimeout(() => {
          navigate(`/${sellerName}/order/confirm?payment_method=cybersource&status=success&orderNumber=${orderNumber}`, {
            state: {
              orderNumber,
              paymentMethod: 'cybersource',
              transactionId: response.data.transactionId,
              status: response.data.status
            }
          });
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Payment failed');
      }

    } catch (err) {
      console.error('CyberSource payment error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Payment failed. Please try again.';
      setError(errorMessage);
      
      // Also redirect to confirm order page with error status
      setTimeout(() => {
        navigate(`/${sellerName}/order/confirm?payment_method=cybersource&status=error&orderNumber=${orderNumber}&error=${encodeURIComponent(errorMessage)}`, {
          state: {
            orderNumber,
            paymentMethod: 'cybersource',
            status: 'error',
            error: errorMessage
          }
        });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center py-8">
        <FaCheckCircle className="text-green-500 text-4xl mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-green-700 mb-2">Payment Successful!</h3>
        <p className="text-gray-600">Redirecting to confirmation page...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Security Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <FaLock className="text-blue-600" />
          <span className="text-sm font-medium text-blue-800">Secure Payment with CyberSource</span>
        </div>
        <p className="text-xs text-blue-700">
          Your payment information is encrypted and processed securely by CyberSource.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Card Details */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Card Information</h3>
          
          {/* Card Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Card Number *
            </label>
            <input
              type="text"
              value={cardDetails.number}
              onChange={handleCardNumberChange}
              placeholder="1234 5678 9012 3456"
              maxLength="19"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          {/* Expiry and CVV */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expiry Date *
              </label>
              <input
                type="text"
                value={`${cardDetails.expirationMonth}${cardDetails.expirationYear}`}
                onChange={handleExpiryChange}
                placeholder="MM/YY"
                maxLength="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CVV *
              </label>
              <input
                type="text"
                value={cardDetails.securityCode}
                onChange={(e) => handleInputChange('card.securityCode', e.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="123"
                maxLength="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          {/* Cardholder Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cardholder Name *
            </label>
            <input
              type="text"
              value={cardDetails.cardholderName}
              onChange={(e) => handleInputChange('card.cardholderName', e.target.value)}
              placeholder="John Doe"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>

        {/* Billing Address */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">Billing Address</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                First Name *
              </label>
            <input
              type="text"
              value={billingAddress.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              placeholder="John (min 2 characters)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Last Name *
              </label>
            <input
              type="text"
              value={billingAddress.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              placeholder="Doe (min 2 characters)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              value={billingAddress.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <input
              type="text"
              value={billingAddress.address1}
              onChange={(e) => handleInputChange('address1', e.target.value)}
              placeholder="123 Main Street (min 5 characters)"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City
              </label>
              <input
                type="text"
                value={billingAddress.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="San Francisco (min 2 characters)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                State
              </label>
              <input
                type="text"
                value={billingAddress.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                placeholder="e.g., CA, NY, TX or California, New York"
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter 2-letter state code (CA, NY) or full state name (California, New York)
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Postal Code
              </label>
              <input
                type="text"
                value={billingAddress.postalCode}
                onChange={(e) => handleInputChange('postalCode', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <FaExclamationTriangle className="text-red-500" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin" />
              Processing Payment...
            </>
          ) : (
            <>
              <FaCreditCard />
              Pay {currencySymbol}{price.toFixed(2)} with CyberSource
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default CyberSource;
